
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { v4 as uuidv4 } from "uuid";

export function useFileUpload(bucketName: string = 'attachments') {
  const [isUploading, setIsUploading] = useState(false);
  
  const uploadFile = async (file: File, path: string = '') => {
    if (!file) {
      return { data: null, error: new Error('No file provided') };
    }
    
    setIsUploading(true);
    
    try {
      // Create a unique file name - replace spaces and special characters
      const fileNameBase = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const fileExt = fileNameBase.split('.').pop();
      const uniqueId = uuidv4().substring(0, 8);
      const fileName = `${uniqueId}_${fileNameBase}`;
      const filePath = path ? `${path}/${fileName}` : fileName;
      
      // Check if bucket exists and create it if needed
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.some(bucket => bucket.name === bucketName);
      
      if (!bucketExists) {
        console.log(`Creating bucket ${bucketName}`);
        const { error: createError } = await supabase.storage.createBucket(bucketName, {
          public: true,
          fileSizeLimit: 52428800 // 50MB
        });
        
        if (createError) {
          console.error(`Error creating bucket ${bucketName}:`, createError);
        }
      }
      
      // Add retry logic for more reliable uploads
      let attempts = 0;
      const maxAttempts = 3;
      let uploadSuccess = false;
      let uploadError = null;
      
      while (attempts < maxAttempts && !uploadSuccess) {
        try {
          console.log(`Uploading file attempt ${attempts + 1}/${maxAttempts}: ${filePath}`);
          
          const { error } = await supabase.storage
            .from(bucketName)
            .upload(filePath, file, {
              upsert: true,
              cacheControl: '3600'
            });
            
          if (error) {
            console.error(`Upload attempt ${attempts + 1} failed:`, error);
            uploadError = error;
            attempts++;
            
            if (attempts < maxAttempts) {
              console.log(`Retrying in 1 second... (attempt ${attempts + 1}/${maxAttempts})`);
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } else {
            uploadSuccess = true;
            break;
          }
        } catch (error) {
          console.error(`Exception in upload attempt ${attempts + 1}:`, error);
          uploadError = error;
          attempts++;
          
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      if (!uploadSuccess) {
        throw uploadError || new Error('Upload failed after multiple attempts');
      }
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);
        
      if (!urlData) {
        throw new Error('Failed to get public URL');
      }
      
      console.log(`Successfully uploaded file to ${filePath}`);
      
      return {
        data: {
          url: urlData.publicUrl,
          name: file.name,
          size: file.size,
          type: file.type,
          path: filePath
        },
        error: null
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      return {
        data: null,
        error
      };
    } finally {
      setIsUploading(false);
    }
  };
  
  const deleteFile = async (path: string) => {
    try {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([path]);
        
      if (error) {
        throw error;
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Erro ao excluir arquivo');
      return { error };
    }
  };
  
  return {
    uploadFile,
    deleteFile,
    isUploading
  };
}
